import React, { useState, useEffect } from 'react';
import './App.css';
import { DISCORD_INVITE_URL } from './constants';

// ─── API Types ───────────────────────────────────────────────────────────────

interface LeaderboardReward {
  id: number;
  place: number;
  winnings: number;
  createdAt: string;
  leaderboardId: number;
  percentage: null | number;
}

interface ApiResponse {
  error: boolean;
  leaderboard: {
    id: number;
    name: string;
    slug?: string | null;
    status: string;
    config: { value: number; gameFilters?: unknown | null };
    affiliate: { id: number; code: string; username: string; avatar: string };
    leaderboardRewards: LeaderboardReward[];
    creatorPrizeUsd?: number;
    bonusPrizeUsd?: number;
    isSponsored?: boolean;
  };
  currentEntry: null | {
    id: number;
    start: string;
    end: string;
    status: string;
    totalValue: number;
  };
  wagers: Array<{
    rank: number;
    username: string;
    avatar: string | null;
    level: number;
    wager: number;
  }>;
  totalParticipants: number;
  refresh?: {
    lastAt: string | null;
    nextAt: string | null;
  };
}

interface HistoryWinner {
  id: number;
  userId: number | null;
  username: string;
  avatar: string | null;
  place: number;
  wager: number;
  payout: number;
}

interface HistoryEntry {
  id: number;
  leaderboardId: number;
  leaderboardName: string;
  leaderboardSlug: string | null;
  startDate: string;
  endDate: string;
  totalValue: number;
  winners: HistoryWinner[];
  createdAt: string;
}

interface HistoryResponse {
  error: boolean;
  history: HistoryEntry[];
}

interface AllTimeRow {
  username: string;
  avatar: string | null;
  totalWager: number;
  totalPayout: number;
  cycles: number;
  wins: number;
  bestPlace: number;
  lastSeen: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const COIN_ICON = 'https://b.site/assets/chicken-coin-ClfWhy9Q.webp';
const REDLEGACY_LEADERBOARD_ID = 46;

// Wager amounts are in b.site coins (not USD)
const fmtCoinsFull = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseDateValue = (value: string | number) => {
  const raw = typeof value === 'number' || /^\d+$/.test(String(value)) ? Number(value) : value;
  return new Date(raw);
};

const fmtDate = (value: string | number) => {
  const date = parseDateValue(value);
  if (Number.isNaN(date.getTime())) return 'TBD';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const fmtDateShort = (value: string | number) => {
  const date = parseDateValue(value);
  if (Number.isNaN(date.getTime())) return 'TBD';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const titleCase = (value: string) =>
  value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');


const RANK_COLORS = [
  '#e8162b', '#9333ea', '#d97706', '#2563eb', '#db2777',
  '#0891b2', '#059669', '#7c3aed', '#65a30d', '#dc2626',
  '#9333ea', '#2563eb', '#d97706', '#db2777', '#0891b2',
];

function rankColor(rank: number): string {
  return RANK_COLORS[(rank - 1) % RANK_COLORS.length];
}

function ordinalLabel(place: number): string {
  if (place === 1) return '1st';
  if (place === 2) return '2nd';
  if (place === 3) return '3rd';
  return `${place}th`;
}

function medalEmoji(place: number): string {
  if (place === 1) return '🥇';
  if (place === 2) return '🥈';
  if (place === 3) return '🥉';
  return String(place);
}

function AccentRedText({ text }: { text: string }) {
  const parts = text.split(/(red)/gi);
  return (
    <>
      {parts.map((part, index) => (
        /red/i.test(part) ? (
          <span key={`${part}-${index}`} className="inline-red-text">{part}</span>
        ) : (
          <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
        )
      ))}
    </>
  );
}


// ─── Components ──────────────────────────────────────────────────────────────

function Avatar({
  src,
  letter,
  color,
  size = 36,
}: {
  src?: string | null;
  letter: string;
  color: string;
  size?: number;
}) {
  const lighten = (hex: string, amount = 30) => {
    const n = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, (n >> 16) + amount);
    const g = Math.min(255, ((n >> 8) & 0xff) + amount);
    const b = Math.min(255, (n & 0xff) + amount);
    return `rgb(${r},${g},${b})`;
  };

  if (src) {
    return (
      <img
        className="player-avatar"
        src={src}
        alt={letter}
        style={{ width: size, height: size, objectFit: 'cover' }}
      />
    );
  }

  return (
    <div
      className="player-avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        background: `linear-gradient(135deg, ${color} 0%, ${lighten(color)} 100%)`,
      }}
    >
      {letter}
    </div>
  );
}

function Countdown({ endMs }: { endMs: number }) {
  const calc = () => {
    const diff = Math.max(0, endMs - Date.now());
    const totalSecs = Math.floor(diff / 1000);
    const d = Math.floor(totalSecs / 86400);
    const h = Math.floor((totalSecs % 86400) / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    const s = totalSecs % 60;
    return { d, h, m, s };
  };

  const [time, setTime] = useState(calc);

  useEffect(() => {
    const id = setInterval(() => setTime(calc), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endMs]);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div className="countdown">
      <div className="countdown-label">Resets in</div>
      <div className="countdown-timer">
        {time.d > 0 && (
          <>
            <div className="countdown-unit">
              <span className="countdown-num">{time.d}</span>
              <span className="countdown-unit-label">Days</span>
            </div>
            <span className="countdown-sep">:</span>
          </>
        )}
        <div className="countdown-unit">
          <span className="countdown-num">{pad(time.h)}</span>
          <span className="countdown-unit-label">Hours</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unit">
          <span className="countdown-num">{pad(time.m)}</span>
          <span className="countdown-unit-label">Mins</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unit">
          <span className="countdown-num">{pad(time.s)}</span>
          <span className="countdown-unit-label">Secs</span>
        </div>
      </div>
    </div>
  );
}

function Podium({ wagers, rewards, pointsLabel, coinIcon }: { wagers: ApiResponse['wagers']; rewards: LeaderboardReward[]; pointsLabel: string; coinIcon: string }) {
  const top3 = wagers.slice(0, 3);
  if (top3.length < 3) return null;

  const order = [top3[1], top3[0], top3[2]];
  const cardClass = ['second', 'first', 'third'];
  const badgeClass = ['silver', 'gold', 'bronze'];
  const rankNum = [2, 1, 3];
  const medals = ['🥈', '🥇', '🥉'];

  const rewardMap = new Map(rewards.map(r => [r.place, r.winnings]));

  return (
    <div className="podium-section">
      <div className="podium">
        {order.map((p, i) => {
          const color = rankColor(rankNum[i]);
          const letter = p.username.charAt(0).toUpperCase();
          const prize = rewardMap.get(rankNum[i]);
          return (
            <div key={p.rank} className={`podium-card ${cardClass[i]}`}>
              <div className={`podium-rank-badge ${badgeClass[i]}`}>{rankNum[i]}</div>
              <div
                className="podium-avatar"
                style={{
                  background: p.avatar ? undefined : `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                  width: cardClass[i] === 'first' ? 72 : 60,
                  height: cardClass[i] === 'first' ? 72 : 60,
                  fontSize: cardClass[i] === 'first' ? 26 : 22,
                  margin: '14px auto 12px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {cardClass[i] === 'first' && <div className="podium-crown">👑</div>}
                {p.avatar ? (
                  <img src={p.avatar} alt={p.username} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                ) : (
                  letter
                )}
              </div>
              <div className="podium-username">{p.username}</div>
              <div className="podium-wager-label">{pointsLabel}</div>
              <div
                className="podium-wager"
                style={{
                  color:
                    badgeClass[i] === 'gold'
                      ? 'var(--gold)'
                      : badgeClass[i] === 'silver'
                        ? 'var(--silver)'
                        : 'var(--bronze)',
                }}
              >
                {fmtCoinsFull(p.wager)}
              </div>
              {prize != null && (
                <div style={{ marginTop: 10 }}>
                  <span className="podium-prize">{medals[i]} <img src={coinIcon} alt="coin" style={{ width: 13, height: 13, objectFit: 'contain', verticalAlign: 'middle' }} /> {prize} Prize</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LastCycleWinners({ entry }: { entry: HistoryEntry }) {
  const topWinners = entry.winners.slice(0, 5);

  return (
    <section className="pending-winners-section">
      <div className="pending-winners-header">
        <div>
          <div className="pending-kicker">Latest RedLegacy Results</div>
          <h2>Last Cycle Winners</h2>
          <p>
            {fmtDate(entry.startDate)} to {fmtDate(entry.endDate)}
          </p>
        </div>
        <div className="pending-total-card">
          <span>Total Wagered</span>
          <strong>{fmtCoinsFull(entry.totalValue)}</strong>
        </div>
      </div>

      <div className="pending-winners-grid">
        {topWinners.map(winner => {
          const color = rankColor(winner.place);
          const letter = winner.username.charAt(0).toUpperCase();
          return (
            <div key={winner.id} className={`pending-winner-card place-${winner.place}`}>
              <div className="pending-place">{ordinalLabel(winner.place)}</div>
              <Avatar src={winner.avatar} letter={letter} color={color} size={winner.place === 1 ? 64 : 54} />
              <div className="pending-winner-name">{winner.username}</div>
              <div className="pending-winner-stats">
                <div>
                  <span>Wagered</span>
                  <strong>{fmtCoinsFull(winner.wager)}</strong>
                </div>
                <div>
                  <span>Prize</span>
                  <strong><img src={COIN_ICON} alt="coin" />{winner.payout}</strong>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function buildAllTimeRows(history: HistoryEntry[]): AllTimeRow[] {
  const rows = new Map<string, AllTimeRow>();

  history.forEach(entry => {
    entry.winners.forEach(winner => {
      const key = winner.username.trim().toLowerCase();
      const current = rows.get(key);

      if (!current) {
        rows.set(key, {
          username: winner.username,
          avatar: winner.avatar,
          totalWager: winner.wager,
          totalPayout: winner.payout,
          cycles: 1,
          wins: winner.place === 1 ? 1 : 0,
          bestPlace: winner.place,
          lastSeen: entry.endDate,
        });
        return;
      }

      current.totalWager += winner.wager;
      current.totalPayout += winner.payout;
      current.cycles += 1;
      current.wins += winner.place === 1 ? 1 : 0;
      current.bestPlace = Math.min(current.bestPlace, winner.place);

      const currentLastSeen = parseDateValue(current.lastSeen).getTime();
      const entryEnd = parseDateValue(entry.endDate).getTime();
      if (!Number.isNaN(entryEnd) && (Number.isNaN(currentLastSeen) || entryEnd > currentLastSeen)) {
        current.lastSeen = entry.endDate;
        current.avatar = winner.avatar ?? current.avatar;
        current.username = winner.username;
      }
    });
  });

  return Array.from(rows.values()).sort((a, b) => {
    if (b.totalWager !== a.totalWager) return b.totalWager - a.totalWager;
    if (b.wins !== a.wins) return b.wins - a.wins;
    return a.bestPlace - b.bestPlace;
  });
}

function AllTimeLeaderboard({
  history,
  loading,
  site,
}: {
  history: HistoryEntry[];
  loading: boolean;
  site: SiteConfig;
}) {
  const rows = buildAllTimeRows(history);
  const totalPoints = rows.reduce((sum, row) => sum + row.totalWager, 0);
  const totalPayout = rows.reduce((sum, row) => sum + row.totalPayout, 0);
  const topRow = rows[0];

  return (
    <main className="all-time-page">
      <section className="all-time-hero">
        <div className="pending-kicker">Saved {site.name} History</div>
        <h1><AccentRedText text={`All-Time ${site.name} Leaderboard`} /></h1>
        <p>
          Totals are built from every saved {site.name} winner history cycle. New finished cycles will be added here as they are saved.
        </p>
      </section>

      <section className="all-time-stats">
        <div className="all-time-stat-card">
          <span>Total Points</span>
          <strong>{fmtCoinsFull(totalPoints)}</strong>
        </div>
        <div className="all-time-stat-card">
          <span>Total Prize Paid</span>
          <strong><img src={COIN_ICON} alt="coin" />{totalPayout.toLocaleString()}</strong>
        </div>
        <div className="all-time-stat-card">
          <span>Saved Cycles</span>
          <strong>{rows.length > 0 ? history.length : 0}</strong>
        </div>
        <div className="all-time-stat-card">
          <span>Top Player</span>
          <strong>{topRow ? <AccentRedText text={topRow.username} /> : 'Pending'}</strong>
        </div>
      </section>

      <section className="all-time-board">
        <div className="all-time-board-head">
          <div>
            <div className="pending-kicker">Overall Standings</div>
            <h2>All-Time Points</h2>
          </div>
          <div className="all-time-board-note">
            {loading ? `Loading saved ${site.name} history...` : `${rows.length} players tracked`}
          </div>
        </div>

        <div className="all-time-table">
          <div className="all-time-table-header">
            <div>Rank</div>
            <div>Player</div>
            <div>Points</div>
            <div>Prize</div>
            <div>Cycles</div>
            <div>Best</div>
            <div>Last Seen</div>
          </div>

          {rows.length === 0 && (
            <div className="table-empty-state">
              <div className="table-empty-title">No saved winners yet</div>
              <div className="table-empty-copy">Once history is saved, the all-time leaderboard will appear here.</div>
            </div>
          )}

          {rows.map((row, index) => {
            const rank = index + 1;
            const color = rankColor(rank);
            const letter = row.username.charAt(0).toUpperCase();

            return (
              <div key={row.username.toLowerCase()} className="all-time-row">
                <div className="all-time-rank">
                  <span className={`rank-num rank-${rank}`}>{rank}</span>
                </div>
                <div className="player-cell">
                  <Avatar src={row.avatar} letter={letter} color={color} />
                  <div>
                    <div className="player-name"><AccentRedText text={row.username} /></div>
                    <div className="player-tag">{row.wins > 0 ? `${row.wins} first-place finish${row.wins === 1 ? '' : 'es'}` : 'Winner history'}</div>
                  </div>
                </div>
                <div className="all-time-mobile-stat">
                  <span>Points</span>
                  <strong className="gold">{fmtCoinsFull(row.totalWager)}</strong>
                </div>
                <div className="all-time-mobile-stat">
                  <span>Prize</span>
                  <strong className="green"><img src={COIN_ICON} alt="coin" />{row.totalPayout.toLocaleString()}</strong>
                </div>
                <div className="all-time-mobile-stat">
                  <span>Cycles</span>
                  <strong>{row.cycles}</strong>
                </div>
                <div className="all-time-mobile-stat">
                  <span>Best</span>
                  <strong>{ordinalLabel(row.bestPlace)}</strong>
                </div>
                <div className="all-time-mobile-stat">
                  <span>Last Seen</span>
                  <strong>{fmtDateShort(row.lastSeen)}</strong>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}

// ─── Sites ───────────────────────────────────────────────────────────────────

type ColumnKey = 'points' | 'games' | 'wins' | 'prize';

interface SiteConfig {
  id: string;
  name: string;
  icon: string;
  color: string;
  live: boolean;
  /** Column labels — omit a key to hide that column entirely */
  columns: Partial<Record<ColumnKey, string>>;
  /** Label for the stat-bar "total" chip (default: "Total Points") */
  totalLabel?: string;
  /** Affiliate code to show in the hero banner */
  affiliateCode?: string;
  /** Full referral URL (when code is a link rather than a text code) */
  affiliateUrl?: string;
  /** Icon shown next to wager/prize amounts */
  coinIcon?: string;
}

const SITES: SiteConfig[] = [
  {
    id: 'bsite',
    name: 'BSite',
    icon: 'https://b.site/favicons/favicon.svg',
    color: '#e8162b',
    live: true,
    columns: { points: 'Points', prize: 'Prize' },
    totalLabel: 'Total Points',
    affiliateCode: 'RED',
  },
  {
    id: 'packy',
    name: 'Packy',
    icon: '/packy-icon.png',
    color: '#8b5cf6',
    live: true,
    columns: { points: 'Wagered (USD)', prize: 'Prize' },
    totalLabel: 'Total Wagered (USD)',
    affiliateCode: 'redlegacy',
    affiliateUrl: 'https://packy.gg/r/redlegacy',
    coinIcon: '/packy-coin.svg',
  },
];

// ─── Coming Soon ─────────────────────────────────────────────────────────────

function ComingSoon({ site }: { site: SiteConfig }) {
  return (
    <div className="coming-soon-wrap">
      <div
        className="coming-soon-card"
        style={{ borderColor: site.color, boxShadow: `0 0 32px ${site.color}33` }}
      >
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <div
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: 28,
            fontWeight: 700,
            color: '#fff',
            marginBottom: 10,
          }}
        >
          Coming Soon
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', maxWidth: 320 }}>
          Leaderboard for <strong style={{ color: site.color }}>{site.name}</strong> launching soon. Stay tuned!
        </div>
      </div>
    </div>
  );
}

// ─── Site Tabs ────────────────────────────────────────────────────────────────

function SiteTabs({
  sites,
  activeId,
  onSelect,
}: {
  sites: SiteConfig[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="site-tabs">
      {sites.map(site => (
        <button
          key={site.id}
          className={`site-tab${activeId === site.id ? ' active' : ''}`}
          onClick={() => onSelect(site.id)}
          style={
            activeId === site.id
              ? ({
                '--tab-accent': site.color,
                borderBottomColor: site.color,
                background: `${site.color}18`,
              } as React.CSSProperties)
              : undefined
          }
        >
          {site.icon.startsWith('http') || site.icon.startsWith('/') ? (
            <img src={site.icon} alt={site.name} className="site-tab-logo" />
          ) : (
            <span className="site-tab-icon">{site.icon}</span>
          )}
          <span className="site-tab-name">{site.name}</span>
          {!site.live && <span className="site-tab-soon">Soon</span>}
        </button>
      ))}
    </div>
  );
}

// ─── App ─────────────────────────────────────────────────────────────────────

interface KickStatus {
  live: boolean;
  viewers: number;
  title: string;
  url: string;
}

function App() {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSiteId, setActiveSiteId] = useState<string>(() => {
    const saved = localStorage.getItem('activeSiteId');
    return (saved && SITES.some(s => s.id === saved)) ? saved : 'bsite';
  });
  const packyPollTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [kickStatus, setKickStatus] = useState<KickStatus | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [packyHistory, setPackyHistory] = useState<HistoryEntry[]>([]);

  const activeSite = SITES.find(s => s.id === activeSiteId) ?? SITES[0];
  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const isAllTimePage = currentPath === '/all-time' || currentPath === '/alltime';
  const isLeaderboardPage = currentPath === '/' || currentPath === '/leaderboard';

  const fetchData = async (): Promise<ApiResponse | null> => {
    try {
      let proxyUrl: string;
      if (activeSiteId === 'packy') {
        proxyUrl = process.env.NODE_ENV === 'development'
          ? 'https://www.redlegacy.site/proxy-packy.php'
          : '/proxy-packy.php';
      } else {
        proxyUrl = process.env.NODE_ENV === 'development'
          ? 'https://www.redlegacy.site/proxy.php'
          : '/proxy.php';
      }
      const res = await fetch(proxyUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: ApiResponse = await res.json();
      if (json.error) throw new Error('API returned an error');
      setData(json);
      setError(null);
      return json;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load leaderboard');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem('activeSiteId', activeSiteId);
  }, [activeSiteId]);

  useEffect(() => {
    if (packyPollTimer.current) clearTimeout(packyPollTimer.current);

    const schedulePackyPoll = (json: ApiResponse) => {
      const nextAt = json.refresh?.nextAt;
      if (!nextAt) return;
      const msUntilNext = Math.max(30_000, new Date(nextAt).getTime() - Date.now() + 2_000);
      packyPollTimer.current = setTimeout(() => {
        fetchData().then(d => { if (d) schedulePackyPoll(d); });
      }, msUntilNext);
    };

    if (activeSiteId === 'packy') {
      fetchData().then(d => { if (d) schedulePackyPoll(d); });
      return () => { if (packyPollTimer.current) clearTimeout(packyPollTimer.current); };
    } else {
      fetchData();
      const id = setInterval(fetchData, 60_000);
      return () => clearInterval(id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSiteId]);

  useEffect(() => {
    const historyUrls = process.env.NODE_ENV === 'development'
      ? ['https://www.redlegacy.site/proxy-history.php', '/redlegacy-data/winners-history.json']
      : ['/proxy-history.php', '/redlegacy-data/winners-history.json'];

    const fetchHistory = async () => {
      for (const historyUrl of historyUrls) {
        try {
          const res = await fetch(historyUrl);
          if (!res.ok) continue;
          const json: HistoryResponse = await res.json();
          if (!json.error && Array.isArray(json.history)) {
            setHistory(json.history);
            return;
          }
        } catch { }
      }
    };

    fetchHistory();
    const id = setInterval(fetchHistory, 5 * 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const url = process.env.NODE_ENV === 'development'
      ? 'https://www.redlegacy.site/packy-data/winners-history.json'
      : '/packy-data/winners-history.json';
    const load = async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const json = await res.json();
        const entries: HistoryEntry[] = Array.isArray(json)
          ? json
          : Array.isArray(json.history) ? json.history : [];
        if (entries.length) setPackyHistory(entries);
      } catch { }
    };
    load();
    const id = setInterval(load, 5 * 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const kickUrl = process.env.NODE_ENV === 'development'
      ? 'https://www.redlegacy.site/proxy-kick.php'
      : '/proxy-kick.php';
    const fetchKick = async () => {
      try {
        const res = await fetch(kickUrl);
        if (!res.ok) return;
        setKickStatus(await res.json());
      } catch { }
    };
    fetchKick();
    const id = setInterval(fetchKick, 60_000);
    return () => clearInterval(id);
  }, []);

  const rewards = data?.leaderboard.leaderboardRewards ?? [];
  const wagers = data?.wagers ?? [];
  const restWagers = wagers.slice(3);
  const totalPrize = rewards.reduce((a, r) => a + r.winnings, 0);
  const currentEntry = data?.currentEntry ?? null;
  const hasActiveEntry = currentEntry !== null;
  const redlegacyHistory = history.filter(entry =>
    entry.leaderboardId === REDLEGACY_LEADERBOARD_ID || /redlegacy/i.test(entry.leaderboardName)
  );
  const latestHistory = redlegacyHistory[0] ?? null;
  const totalValue = currentEntry?.totalValue ?? 0;
  const totalParticipants = data?.totalParticipants ?? 0;
  const displayTotalValue = hasActiveEntry ? totalValue : latestHistory?.totalValue ?? totalValue;
  const displayParticipants = hasActiveEntry ? totalParticipants : latestHistory?.winners.length ?? totalParticipants;
  const endMs = currentEntry ? parseDateValue(currentEntry.end).getTime() : 0;
  const rewardMap = new Map(rewards.map(r => [r.place, r.winnings]));
  const affiliate = data?.leaderboard.affiliate;
  const entryStatus = currentEntry?.status ? titleCase(currentEntry.status) : data ? 'Pending' : 'Loading';
  const leaderboardStatus = data?.leaderboard.status ? titleCase(data.leaderboard.status) : 'Loading';
  const refreshLabel = currentEntry ? fmtDate(currentEntry.end) : 'Waiting for next cycle';
  const startLabel = currentEntry ? fmtDate(currentEntry.start) : 'Waiting for next cycle';
  const rewardSlots = rewards.length;
  const isSponsored = data?.leaderboard.isSponsored ?? false;
  const creatorPrize = data?.leaderboard.creatorPrizeUsd ?? 0;
  const bonusPrize = data?.leaderboard.bonusPrizeUsd ?? 0;
  const lastRefreshedAt = data?.refresh?.lastAt ?? null;
  const nextUpdateAt = data?.refresh?.nextAt ?? null;
  const isPreparingStandings = activeSiteId === 'packy' && lastRefreshedAt === null && !loading;
  const lastRefreshedLabel = lastRefreshedAt
    ? (() => {
        const diff = Math.floor((Date.now() - new Date(lastRefreshedAt).getTime()) / 60_000);
        return diff < 1 ? 'just now' : `${diff}m ago`;
      })()
    : null;

  // Build grid template from active site columns
  const COL_WIDTHS: Record<ColumnKey, string> = {
    points: '1fr',
    games: '130px',
    wins: '110px',
    prize: '160px',
  };
  const activeColKeys = (['points', 'games', 'wins', 'prize'] as ColumnKey[]).filter(
    k => activeSite.columns[k]
  );
  const gridCols = ['60px', '1fr', ...activeColKeys.map(k => COL_WIDTHS[k])].join(' ');

  return (
    <div className="app" style={{ '--site-accent': activeSite.color } as React.CSSProperties}>
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-top">
          <span className="navbar-logo">
            <img src="/redlegacy-logo.png" alt="Red Legacy" className="navbar-logo-img" />
          </span>
          <ul className="navbar-links">
            <li><a href="/" className={currentPath === '/' ? 'active' : undefined}>Home</a></li>
            <li><a href="/leaderboard" className={isLeaderboardPage && currentPath !== '/' ? 'active' : undefined}>Leaderboard</a></li>
            <li><a href="/all-time" className={isAllTimePage ? 'active' : undefined}>All-Time</a></li>
            <li><a href="/affiliates" className={currentPath === '/affiliates' ? 'active' : undefined}>Affiliates</a></li>
          </ul>
          <div className="navbar-right">
            <a
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="discord-badge"
            >
              <img src="/discord-logo.svg" alt="" className="discord-badge-icon" aria-hidden="true" />
              <span>Discord</span>
            </a>
            <a
              href={kickStatus?.url ?? 'https://kick.com/redlegacy23'}
              target="_blank"
              rel="noopener noreferrer"
              className={`kick-live-badge${kickStatus?.live ? ' is-live' : ' is-offline'}`}
            >
              {kickStatus?.live ? (
                <>
                  <span className="kick-live-dot" />
                  <img src="/kick-logo.svg" alt="Kick" className="kick-logo-icon" />
                  <span>LIVE</span>
                  {kickStatus.viewers > 0 && (
                    <span className="kick-viewers">{kickStatus.viewers.toLocaleString()}</span>
                  )}
                  {kickStatus.title && (
                    <span className="kick-live-title">{kickStatus.title}</span>
                  )}
                </>
              ) : (
                <>
                  <img src="/kick-logo.svg" alt="Kick" className="kick-logo-icon" />
                  <span>Watch on Kick</span>
                </>
              )}
            </a>
          </div>
        </div>
        <SiteTabs sites={SITES} activeId={activeSiteId} onSelect={setActiveSiteId} />
        {data && hasActiveEntry && (
          <div className="top-countdown-notice">
            <Countdown endMs={endMs} />
          </div>
        )}
      </nav>

      {isAllTimePage ? (
        <AllTimeLeaderboard
          history={activeSiteId === 'packy' ? packyHistory : redlegacyHistory}
          loading={activeSiteId === 'packy' ? packyHistory.length === 0 : history.length === 0}
          site={activeSite}
        />
      ) : (
        <>
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg" />
        {(activeSite.affiliateCode || data) && (
          <div className="hero-promo-row">
            {activeSite.affiliateCode && (
              activeSite.affiliateUrl ? (
                <a href={activeSite.affiliateUrl} target="_blank" rel="noopener noreferrer" className="hero-code-banner hero-code-banner--link">
                  <span className="hero-code-label">Use Code</span>
                  <span className="hero-code-value">{activeSite.affiliateCode}</span>
                  <span className="hero-code-label">on {activeSite.name}</span>
                </a>
              ) : (
                <div className="hero-code-banner">
                  <span className="hero-code-label">Use Code</span>
                  <span className="hero-code-value">{activeSite.affiliateCode}</span>
                  <span className="hero-code-label">on {activeSite.name}</span>
                </div>
              )
            )}

          </div>
        )}
        <img src="/redlegacy-logo.png" alt="Red Legacy" className="hero-logo" />
        <div className="hero-badge-row">
          <div className="hero-badge">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
            </svg>
            <AccentRedText text={data?.leaderboard.name ?? `${activeSite.name} Leaderboard`} />
          </div>
          {isSponsored && (
            <div className="sponsored-badge">🎁 Sponsored by Packy</div>
          )}
        </div>
        <h1 className="hero-title">
          Compete. <span className="red">Wager.</span> Win.
        </h1>

        <p className="hero-subtitle">
          {affiliate
            ? (
              <>
                Track the live {activeSite.name} race hosted by <AccentRedText text={affiliate.username} />. Climb the ranks and claim your share of the prize pool.
              </>
            )
            : `Top players by points earned on ${activeSite.name}. Climb the ranks and claim your share of the prize pool.`}
        </p>
        {data && (
          <>
            <div className="stats-bar">
              <div className="stat-chip">
                <div className="stat-chip-value gold">{fmtCoinsFull(displayTotalValue)}</div>
                <div className="stat-chip-label">{hasActiveEntry ? activeSite.totalLabel ?? 'Total Wagered' : 'Last Cycle Total'}</div>
              </div>
              <div className="stat-chip">
                <div className="stat-chip-value green"><img src={activeSite.coinIcon ?? COIN_ICON} alt="coin" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 3 }} />{totalPrize.toLocaleString()}</div>
                <div className="stat-chip-label">Prize Pool</div>
              </div>
              <div className="stat-chip">
                <div className="stat-chip-value">{displayParticipants}</div>
                <div className="stat-chip-label">{hasActiveEntry ? 'Contestants' : 'Winners'}</div>
              </div>
              <div className="stat-chip">
                <div className="stat-chip-value red">{entryStatus}</div>
                <div className="stat-chip-label">Cycle Status</div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Loading / Error / Content */}
      {!activeSite.live ? (
        <ComingSoon site={activeSite} />
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid rgba(232,22,43,0.2)',
            borderTopColor: 'var(--red)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '80px 40px', color: 'var(--red-light)' }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Failed to load leaderboard</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>{error}</div>
          <button
            className="btn-signup"
            onClick={() => { setLoading(true); fetchData(); }}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Podium */}
          <Podium wagers={wagers} rewards={rewards} pointsLabel={activeSite.columns.points ?? activeSite.totalLabel ?? 'Points'} coinIcon={activeSite.coinIcon ?? COIN_ICON} />
          {!hasActiveEntry && latestHistory && <LastCycleWinners entry={latestHistory} />}
          {/* Main leaderboard + sidebar */}
          <div className="main-content">
            <div className="content-grid">
              <div>
                <div className="hero-meta-grid leaderboard-meta-grid">
                  <div className="hero-meta-card">
                    <div className="hero-meta-label">Affiliate Code</div>
                    {activeSite.affiliateUrl ? (
                      <a href={activeSite.affiliateUrl} target="_blank" rel="noopener noreferrer" className="hero-meta-value hero-meta-link">
                        <AccentRedText text={activeSite.affiliateCode ?? 'redlegacy'} />
                      </a>
                    ) : (
                      <div className="hero-meta-value"><AccentRedText text={affiliate?.code ?? activeSite.affiliateCode ?? 'RED'} /></div>
                    )}
                    <div className="hero-meta-subvalue">{activeSite.affiliateUrl ? `Sign up via our link` : `Use this code on ${activeSite.name}`}</div>
                  </div>
                  <div className="hero-meta-card">
                    <div className="hero-meta-label">Current Cycle</div>
                    <div className="hero-meta-value">{currentEntry ? fmtDateShort(currentEntry.start) : 'TBD'}</div>
                    <div className="hero-meta-subvalue">Started {startLabel}</div>
                  </div>
                  <div className="hero-meta-card">
                    <div className="hero-meta-label">Resets</div>
                    <div className="hero-meta-value">{currentEntry ? fmtDateShort(currentEntry.end) : 'TBD'}</div>
                    <div className="hero-meta-subvalue">{refreshLabel}</div>
                  </div>
                  <div className="hero-meta-card">
                    <div className="hero-meta-label">Reward Slots</div>
                    <div className="hero-meta-value">{rewardSlots}</div>
                    <div className="hero-meta-subvalue">{entryStatus} cycle</div>
                  </div>
                </div>

                {/* Live indicator */}
                <div className="tabs-row">
                  <div className="tabs-right">
                    <div className="live-dot" />
                    {activeSiteId === 'packy' ? (
                      lastRefreshedLabel
                        ? `Updated ${lastRefreshedLabel}${nextUpdateAt ? ` · next ~${Math.max(0, Math.ceil((new Date(nextUpdateAt).getTime() - Date.now()) / 60_000))}m` : ''}`
                        : isPreparingStandings ? 'Preparing standings…' : 'Waiting for refresh'
                    ) : (
                      hasActiveEntry ? 'Updates every 60 seconds' : 'Showing latest saved results while pending'
                    )}
                  </div>
                </div>

                {/* Table */}
                <div className="leaderboard-table">
                  <div className="table-header" style={{ gridTemplateColumns: gridCols }}>
                    <div className="th">Rank</div>
                    <div className="th">Player</div>
                    {activeSite.columns.points && <div className="th right">{activeSite.columns.points}</div>}
                    {activeSite.columns.games && <div className="th right">{activeSite.columns.games}</div>}
                    {activeSite.columns.wins && <div className="th right">{activeSite.columns.wins}</div>}
                    {activeSite.columns.prize && <div className="th right">{activeSite.columns.prize}</div>}
                  </div>

                  {restWagers.length === 0 && (
                    <div className="table-empty-state">
                      {isPreparingStandings ? (
                        <>
                          <div className="table-empty-title">Preparing standings…</div>
                          <div className="table-empty-copy">Packy is building the leaderboard. Check back in a few minutes.</div>
                        </>
                      ) : (
                        <>
                          <div className="table-empty-title">New leaderboard cycle pending</div>
                          <div className="table-empty-copy">
                            The last cycle has ended. Winners are shown above until {activeSite.name} publishes the next active entry.
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {restWagers.map(w => {
                    const color = rankColor(w.rank);
                    const letter = w.username.charAt(0).toUpperCase();
                    const prize = rewardMap.get(w.rank);
                    return (
                      <div key={w.rank} className="table-row" style={{ gridTemplateColumns: gridCols }}>
                        <div className="rank-cell">
                          <span className={`rank-num rank-${w.rank}`}>{w.rank}</span>
                        </div>
                        <div className="player-cell">
                          <Avatar src={w.avatar} letter={letter} color={color} />
                          <div>
                            <div className="player-name">{w.username}</div>
                            <div className="player-tag">Lvl {w.level}</div>
                          </div>
                        </div>
                        <div className="table-row-stats">
                          {activeSite.columns.points && (
                            <div className="mobile-stat">
                              <div className="mobile-stat-label">{activeSite.columns.points}</div>
                              <div className="cell-value gold">{fmtCoinsFull(w.wager)}</div>
                            </div>
                          )}
                          {activeSite.columns.games && (
                            <div className="mobile-stat">
                              <div className="mobile-stat-label">{activeSite.columns.games}</div>
                              <div className="cell-value muted">—</div>
                            </div>
                          )}
                          {activeSite.columns.wins && (
                            <div className="mobile-stat">
                              <div className="mobile-stat-label">{activeSite.columns.wins}</div>
                              <div className="cell-value muted">—</div>
                            </div>
                          )}
                          {activeSite.columns.prize && (
                            <div className="mobile-stat">
                              <div className="mobile-stat-label">{activeSite.columns.prize}</div>
                              <div className="prize-cell">
                                {prize != null ? (
                                  <span className="prize-badge"><img src={activeSite.coinIcon ?? COIN_ICON} alt="coin" style={{ width: 13, height: 13, objectFit: 'contain', verticalAlign: 'middle' }} /> {prize}</span>
                                ) : (
                                  <span className="prize-badge empty">—</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sidebar */}
              <div className="sidebar">
                <div className="info-card">
                  <div className="info-card-title">Season Details</div>
                  <div className="detail-list">
                    <div className="detail-row">
                      <span className="detail-label">Leaderboard</span>
                      <span className="detail-value"><AccentRedText text={data?.leaderboard.name ?? 'Red Legacy'} /></span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Host</span>
                      <span className="detail-value"><AccentRedText text={affiliate?.username ?? 'Red Legacy'} /></span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Affiliate Code</span>
                      <span className="detail-value"><AccentRedText text={affiliate?.code ?? activeSite.affiliateCode ?? 'RED'} /></span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Leaderboard Status</span>
                      <span className="detail-value">{leaderboardStatus}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Entry Status</span>
                      <span className="detail-value">{entryStatus}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Cycle Started</span>
                      <span className="detail-value">{startLabel}</span>
                    </div>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-card-title">Prize Pool</div>
                  <ul className="prize-pool-list">
                    {rewards.map(item => (
                      <li key={item.id} className="prize-pool-item">
                        <span className="prize-pool-rank">
                          <span className="medal">{medalEmoji(item.place)}</span>
                          {ordinalLabel(item.place)} Place
                        </span>
                        <span className="prize-pool-amount"><img src={activeSite.coinIcon ?? COIN_ICON} alt="coin" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 3 }} />{item.winnings}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="divider" />
                  {creatorPrize > 0 && (
                    <>
                      <div className="prize-pool-item" style={{ opacity: 0.75 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Creator Prize</span>
                        <span className="prize-pool-amount" style={{ fontSize: 14 }}>
                          <img src={activeSite.coinIcon ?? COIN_ICON} alt="coin" style={{ width: 13, height: 13, objectFit: 'contain', verticalAlign: 'middle', marginRight: 3 }} />{creatorPrize.toLocaleString()}
                        </span>
                      </div>
                      <div className="prize-pool-item" style={{ opacity: 0.75 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>🎁 Packy Bonus</span>
                        <span className="prize-pool-amount" style={{ fontSize: 14, color: '#8b5cf6' }}>
                          +{bonusPrize.toLocaleString()}
                        </span>
                      </div>
                      <div className="divider" />
                    </>
                  )}
                  <div className="prize-pool-item" style={{ fontWeight: 700 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                      Total Pool
                    </span>
                    <span className="prize-pool-amount" style={{ fontSize: 18 }}>
                      <img src={activeSite.coinIcon ?? COIN_ICON} alt="coin" style={{ width: 15, height: 15, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />{totalPrize.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="info-card">
                  <div className="info-card-title">Time Remaining</div>
                  {hasActiveEntry ? (
                    <Countdown endMs={endMs} />
                  ) : (
                    <div className="cycle-pending-message">
                      Waiting for BSite to start the next leaderboard cycle.
                    </div>
                  )}
                </div>

                <div className="info-card">
                  <div className="info-card-title">How It Works</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                    <p style={{ marginBottom: 10 }}>
                      Use code <strong style={{ color: 'var(--red-light)' }}><AccentRedText text={affiliate?.code ?? 'RED'} /></strong> on {activeSite.name} to start tracking your wagers.
                    </p>
                    <p style={{ marginBottom: 10 }}>
                      {hasActiveEntry ? (
                        <>
                          This cycle started on <strong style={{ color: '#fff' }}>{fmtDateShort(currentEntry?.start ?? 0)}</strong> and currently pays out <strong style={{ color: '#fff' }}>{rewardSlots}</strong> places.
                        </>
                      ) : (
                        <>
                          The previous cycle has ended. New wagers will show here once BSite starts the next leaderboard cycle.
                        </>
                      )}
                    </p>
                    <p>
                      Prizes are distributed automatically at the end of each cycle.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
        </>
      )}

      {/* Footer */}
      <footer className="footer">
        {activeSite.affiliateCode && (
          <div className="footer-code-banner">
            <span className="hero-code-label">Use Code</span>
            <span className="hero-code-value">{activeSite.affiliateCode}</span>
            <span className="hero-code-label">on {activeSite.name}</span>
          </div>
        )}
        <img src="/red-footer.png" alt="Red Legacy" className="footer-banner-img" />
        <p className="footer-text">
          <AccentRedText text="Red Legacy is an affiliate leaderboard platform." /> Gambling involves risk. Please play responsibly. 18+ only.
        </p>
      </footer>
    </div>
  );
}

export default App;
